import {
  useState, createContext, useContext, useMemo, useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentCustomer } from '../../../services/customer/profileServices';
import { getGuestTicketBookingForm } from '../../../services/customer/ticketBookingServices';
import useTranslation from '../../hooks/translation/useTranslation';
import { getRidePromotionsService } from '../../../services/admin/ridePromotionServices';
import buildRidePromotionsMap from '../../../utils/functions/ridePromotions/buildRidePromotionsMap';

export const UserContext = createContext({
  user: {},
  accessToken: null,
  mobileNavigationAction: null,
  ticketBookingOptions: {
    stations: [],
    ridePromotions: new Map(),
  },
  setUser: () => {},
  resetUser: () => {},
  getCurrentUser: () => {},
  setMobileNavigationAction: () => {},
});

export const useAuth = () => useContext(UserContext);

export function UserContextProvider(props) {
  const { currentLanguage } = useTranslation();
  const { children } = props;
  const [userData, setUserData] = useState({});
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [ticketBookingOptions, setTicketBookingOptions] = useState({
    stations: [],
    suggestedStations: [],
  });
  const [mobileNavigationAction, setMobileNavigationAction] = useState(null);
  const navigate = useNavigate();

  const setUser = async (user, token) => {
    if (user && typeof user === 'object') {
      setUserData(user);
    }
    if (token && typeof token === 'string') {
      setAccessToken(token);
      localStorage.setItem('accessToken', token);
    }
  };
  const resetUser = () => {
    setUserData({});
    localStorage.removeItem('accessToken');
    setAccessToken(null);
    navigate(`/${currentLanguage || 'ka'}`);
  };

  const getCurrentUser = async () => {
    try {
      if (accessToken) {
        const res = await getCurrentCustomer();
        if (res.status === 'success') {
          setUser(res.data.user);
        }
      }
    } catch (e) {
      resetUser();
    }
  };

  useEffect(() => {
    const getTicketBookingOptions = async () => {
      const options = {
        stations: [],
        ridePromotions: new Map(),
      };

      await Promise.all([
        getGuestTicketBookingForm().then((res) => {
          options.stations = res.stations.map((station) => ({
            id: station.station_id,
            label_ka: station?.name_ka,
            label_en: station?.name_en,
            label_ru: station?.name_ru,
            value: station.station_code,
          }));
        }),
        getRidePromotionsService().then((res) => {
          if (res.status === 'success') {
            options.ridePromotions = buildRidePromotionsMap(res.data);
          }
        }),
      ]);
      setTicketBookingOptions(options);
    };
    getTicketBookingOptions();
  }, []);

  const contextValue = useMemo(() => ({
    user: userData,
    setUser,
    accessToken,
    mobileNavigationAction,
    resetUser,
    getCurrentUser,
    ticketBookingOptions,
    setMobileNavigationAction,
  }), [userData, accessToken, mobileNavigationAction, ticketBookingOptions]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}
